import type {
	TopologyBoundaryCurrentMember,
} from '../../../../types/reference/sketch-entity-reference/TopologyBoundaryMemberCorrespondence';
import type {
	TopologyEndpointAnchorEvidence,
} from '../../../../types/reference/sketch-entity-reference/TopologyEndpointAnchorEvidence';
import {resolveTopologyEndpointAnchorId} from '../model/ExternalGeometrySourceDescriptor';
import type {SketchEntityReferenceRecord} from '../model/SketchEntityReferenceRecord';

/**
 * 历史引用端点与当前 topology 边端点的统一证据解析器。
 *
 * 成员匹配和目标端点归属必须消费同一份方向证据，避免两条链路对相同
 * vertexA/vertexB 翻转得出不同结论。本类只比较持久 anchor，不猜测缺失信息。
 */
export class TopologyEndpointAnchorEvidenceResolver {
	/**
	 * 单端命中的成员匹配分值。
	 *
	 * 两端命中总分 8,000，低于边 persistentRef 的 10,000，且高于语义名的 1,000；
	 * 因此端点邻接可区分同一物理边身份下的候选，但不会推翻唯一持久边身份。
	 */
	private static readonly ENDPOINT_MATCH_SCORE = 4_000;

	/**
	 * 比较历史持久端点 anchor 与当前边的 canonical vertex。
	 *
	 * @param reference 历史引用记录。
	 * @param current 当前 Wire 成员。
	 * @returns 同向、反向命中数量及三态方向裁决。
	 */
	public resolve(
		reference: SketchEntityReferenceRecord,
		current: TopologyBoundaryCurrentMember,
	): TopologyEndpointAnchorEvidence {
		// 只有历史绑定和当前 canonical vertex 同时可用时，才允许给出方向证据。
		if (reference.sourceDescriptor.kind !== 'topology-edge') {
			return this.createUnknownEvidence();
		}
		const previousBindings = reference.rebuildPayload.endpointAnchorBindings;
		const currentRefs = current.endpointAnchorRefs;
		if (!previousBindings || (!currentRefs?.start && !currentRefs?.end)) {
			return this.createUnknownEvidence();
		}

		// 将当前局部 vertex ref 规范化到与持久记录一致的逻辑 owner 锚点空间。
		const previousStart = previousBindings.find(
			binding => binding.targetEndpoint === 'start',
		)?.sourceAnchorId;
		const previousEnd = previousBindings.find(
			binding => binding.targetEndpoint === 'end',
		)?.sourceAnchorId;
		const currentStart = currentRefs.start
			? resolveTopologyEndpointAnchorId(current.source.owner, currentRefs.start)
			: undefined;
		const currentEnd = currentRefs.end
			? resolveTopologyEndpointAnchorId(current.source.owner, currentRefs.end)
			: undefined;

		// 分别计算同向和反向证据；分值相同时必须保持 unknown，禁止默认猜方向。
		const directMatches = Number(
			Boolean(previousStart) && previousStart === currentStart,
		) + Number(Boolean(previousEnd) && previousEnd === currentEnd);
		const reverseMatches = Number(
			Boolean(previousStart) && previousStart === currentEnd,
		) + Number(Boolean(previousEnd) && previousEnd === currentStart);
		return {
			directMatches,
			reverseMatches,
			direction: directMatches === reverseMatches
				? 'unknown'
				: directMatches > reverseMatches
					? 'direct'
					: 'reverse',
			score: Math.max(directMatches, reverseMatches)
				* TopologyEndpointAnchorEvidenceResolver.ENDPOINT_MATCH_SCORE,
		};
	}

	/**
	 * 创建不包含任何方向猜测的空证据。
	 *
	 * @returns unknown 方向的零分证据。
	 */
	private createUnknownEvidence(): TopologyEndpointAnchorEvidence {
		return {
			directMatches: 0,
			reverseMatches: 0,
			direction: 'unknown',
			score: 0,
		};
	}
}
